import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { VaultTranslatedBaseComponent } from '@components/base/vault-translated-base.component';
import { Checkbox } from '@interfaces/a-organizar/checkbox';
import { MeetingFilterRequest } from '@interfaces/meetings/requests/meeting.request';
import {
  MeetingCallType,
  MeetingSource,
  MeetingStatus,
} from '@interfaces/meetings/responses/meeting.response';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { SharedModule } from '@shared/shared.module';
import { ActiveFilterConfig } from '@src/app/shared/interfaces/active-filter.interface';
import { DrawerModule } from 'primeng/drawer';
import { first } from 'rxjs';
import {
  MEETING_SOURCE_LABELS,
  MEETING_STATUS_LABELS,
  MEETING_STATUS_OPTIONS,
  MEETING_CALL_TYPE_LABELS,
} from '@features/dashboard/meetings/components/meeting-presenter';
import {
  CLIENT_VISIBILITY_FILTER_LABELS,
  CLIENT_VISIBILITY_FILTER_VALUES,
} from '@features/dashboard/meetings/components/meeting-visibility-presenter';
import { MeetingClientVisibilityFilter } from '@interfaces/meetings/responses/meeting-visibility.response';

/** Filtros de lista e o nome que aparece no pill. */
const FILTROS_DE_LISTA: { chave: keyof MeetingFilterRequest; grupo: string }[] = [
  { chave: 'companyIds', grupo: 'Empresas' },
  { chave: 'partnerIds', grupo: 'Parceiros' },
  { chave: 'financialInstitutionIds', grupo: 'Bancos' },
  { chave: 'dealIds', grupo: 'Deals' },
  { chave: 'statuses', grupo: 'Situações' },
  { chave: 'sources', grupo: 'Origem do bot' },
  { chave: 'callTypes', grupo: 'Tipo da reunião' },
  { chave: 'organizerUserIds', grupo: 'Organizadores' },
  { chave: 'participantEmails', grupo: 'Participantes' },
  { chave: 'clientVisibility', grupo: 'Para o cliente' },
];

/**
 * Barra lateral de filtros da visão geral de ligações (cards 3112 e 3139), no padrão
 * `filter-modal-creation-rule` (modelo: `task-filter`).
 *
 * **Seleção múltipla** (card 3139): valores do mesmo filtro somam (OU), filtros diferentes
 * restringem (E). Empresa, parceiro e banco valem pelo participante reconhecido no e-mail ou pelo
 * vínculo manual (card 3138) — é o backend que decide. As opções vêm de `GET meetings/filters`: só o
 * que aparece em alguma ligação, para nenhum filtro devolver zero linha por construção.
 */
@Component({
  selector: 'app-meetings-filter',
  standalone: true,
  imports: [SharedModule, CommonModule, ReactiveFormsModule, DrawerModule],
  templateUrl: './meetings-filter.component.html',
  styleUrls: ['./meetings-filter.component.scss'],
})
export class MeetingsFilterComponent
  extends VaultTranslatedBaseComponent
  implements OnInit, OnChanges
{
  @Input() isVisible = false;
  @Input() currentFilters: MeetingFilterRequest | null = null;

  @Output() sendCloseFilter = new EventEmitter<void>();
  @Output() sendFilter = new EventEmitter<MeetingFilterRequest>();
  @Output() sendActiveFilters = new EventEmitter<ActiveFilterConfig[]>();

  filterForm: FormGroup = this.formBuilder.group({
    from: [''],
    to: [''],
    companyIds: [[]],
    partnerIds: [[]],
    financialInstitutionIds: [[]],
    dealIds: [[]],
    statuses: [[]],
    sources: [[]],
    callTypes: [[]],
    organizerUserIds: [[]],
    participantEmails: [[]],
    clientVisibility: [[]],
    participantAttendedOnly: [null],
    hasRecording: [null],
    hasTranscript: [null],
    onlyUnlinked: [null],
  });

  companyOptions: Option[] = [];
  partnerOptions: Option[] = [];
  bankOptions: Option[] = [];
  dealOptions: Option[] = [];
  organizerOptions: Option[] = [];
  participantOptions: Option[] = [];
  readonly statusOptions: Option[] = MEETING_STATUS_OPTIONS;
  /** Tipos da reunião classificados pelo Cog. */
  readonly callTypeOptions: Option[] = (
    Object.keys(MEETING_CALL_TYPE_LABELS) as MeetingCallType[]
  ).map(tipo => ({ label: MEETING_CALL_TYPE_LABELS[tipo], value: tipo }));
  readonly sourceOptions: Option[] = (Object.keys(MEETING_SOURCE_LABELS) as MeetingSource[]).map(
    source => ({ label: MEETING_SOURCE_LABELS[source], value: source })
  );
  /** Estado da chave Cliente (card 3803): o mesmo filtro das pílulas "Para o cliente" da lista. */
  readonly clientVisibilityOptions: Option[] = CLIENT_VISIBILITY_FILTER_VALUES.map(value => ({
    label: CLIENT_VISIBILITY_FILTER_LABELS[value],
    value,
  }));
  /** Sim/Não de "com gravação" e "com transcrição"; vazio = tanto faz. */
  readonly simNaoOptions: Option[] = [
    { label: 'Sim', value: 'true' },
    { label: 'Não', value: 'false' },
  ];

  /** O `app-checkbox` é dirigido por objeto: um novo a cada repovoamento reflete o estado. */
  onlyUnlinkedCheckbox: Checkbox = this.criarCheckbox(false);
  attendedOnlyCheckbox: Checkbox = this.criarCheckbox(
    false,
    'Só reuniões em que o participante entrou na call',
    'participantAttendedOnly'
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastHelperService);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly meetingsService: MeetingsHttpService,
    translateService: TranslateService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.carregarOpcoes();
    this.populateFormWithCurrentFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentFilters']) {
      this.populateFormWithCurrentFilters();
    }
  }

  private carregarOpcoes(): void {
    this.meetingsService
      .getFilterOptions()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: opcoes => {
          const paraOpcao = (item: { id: string; name: string }): Option => ({
            label: item.name,
            value: item.id,
          });
          this.companyOptions = (opcoes?.companies ?? []).map(paraOpcao);
          this.partnerOptions = (opcoes?.partners ?? []).map(paraOpcao);
          this.bankOptions = (opcoes?.financialInstitutions ?? []).map(paraOpcao);
          this.dealOptions = (opcoes?.deals ?? []).map(paraOpcao);
          this.organizerOptions = (opcoes?.organizers ?? []).map(paraOpcao);
          this.participantOptions = (opcoes?.participants ?? []).map(paraOpcao);
          // Os pills dependem dos nomes: reemite agora que eles chegaram.
          this.emitActiveFilters();
        },
        // Sem opções o filtro ainda funciona por período, situação, origem e gravação.
        error: () => this.emitActiveFilters(),
      });
  }

  private populateFormWithCurrentFilters(): void {
    const f = this.currentFilters ?? {};
    this.filterForm.patchValue({
      from: f.from ?? '',
      to: f.to ?? '',
      companyIds: [...(f.companyIds ?? [])],
      partnerIds: [...(f.partnerIds ?? [])],
      financialInstitutionIds: [...(f.financialInstitutionIds ?? [])],
      dealIds: [...(f.dealIds ?? [])],
      statuses: [...(f.statuses ?? [])],
      sources: [...(f.sources ?? [])],
      callTypes: [...(f.callTypes ?? [])],
      organizerUserIds: [...(f.organizerUserIds ?? [])],
      participantEmails: [...(f.participantEmails ?? [])],
      clientVisibility: [...(f.clientVisibility ?? [])],
      participantAttendedOnly: f.participantAttendedOnly ? 'true' : null,
      hasRecording: this.paraSimNao(f.hasRecording),
      hasTranscript: this.paraSimNao(f.hasTranscript),
      onlyUnlinked: f.onlyUnlinked ? 'true' : null,
    });
    this.onlyUnlinkedCheckbox = this.criarCheckbox(!!f.onlyUnlinked);
    this.attendedOnlyCheckbox = this.criarCheckbox(
      !!f.participantAttendedOnly,
      'Só reuniões em que o participante entrou na call',
      'participantAttendedOnly'
    );
    this.emitActiveFilters();
  }

  clear(): void {
    this.filterForm.reset({
      companyIds: [],
      partnerIds: [],
      financialInstitutionIds: [],
      dealIds: [],
      statuses: [],
      sources: [],
      callTypes: [],
      organizerUserIds: [],
      participantEmails: [],
      clientVisibility: [],
    });
    this.onlyUnlinkedCheckbox = this.criarCheckbox(false);
    this.attendedOnlyCheckbox = this.criarCheckbox(
      false,
      'Só reuniões em que o participante entrou na call',
      'participantAttendedOnly'
    );
    this.submit();
  }

  submit(): void {
    const v = this.filterForm.value;
    // As datas chegam como `yyyy-MM-dd`: a comparação de texto já é cronológica.
    if (v.from && v.to && String(v.from) > String(v.to)) {
      this.toast.showToastError('Período inválido', 'A data inicial é depois da data final.');
      return;
    }
    const lista = <T>(valor: unknown): T[] =>
      Array.isArray(valor) ? (valor.filter(item => !!item) as T[]) : [];

    const filtro: MeetingFilterRequest = {
      from: v.from || null,
      to: v.to || null,
      companyIds: lista<string>(v.companyIds),
      partnerIds: lista<string>(v.partnerIds),
      financialInstitutionIds: lista<string>(v.financialInstitutionIds),
      dealIds: lista<string>(v.dealIds),
      statuses: lista<MeetingStatus>(v.statuses),
      sources: lista<MeetingSource>(v.sources),
      callTypes: lista<MeetingCallType>(v.callTypes),
      organizerUserIds: lista<string>(v.organizerUserIds),
      participantEmails: lista<string>(v.participantEmails),
      clientVisibility: lista<MeetingClientVisibilityFilter>(v.clientVisibility),
      participantAttendedOnly:
        v.participantAttendedOnly && lista<string>(v.participantEmails).length ? true : null,
      hasRecording: this.deSimNao(v.hasRecording),
      hasTranscript: this.deSimNao(v.hasTranscript),
      onlyUnlinked: v.onlyUnlinked ? true : null,
    };

    (Object.keys(filtro) as (keyof MeetingFilterRequest)[]).forEach(chave => {
      const valor = filtro[chave];
      if (valor === null || valor === '' || (Array.isArray(valor) && !valor.length))
        delete filtro[chave];
    });

    this.sendFilter.emit(filtro);
    this.sendCloseFilter.emit();
  }

  onSidebarHide(): void {
    this.sendCloseFilter.emit();
  }

  /** Um pill por filtro, com os nomes escolhidos. `group` é o nome exibido no pill. */
  private emitActiveFilters(): void {
    const f = this.currentFilters;
    const ativos: ActiveFilterConfig[] = [];
    if (!f) {
      this.sendActiveFilters.emit(ativos);
      return;
    }

    if (f.from) {
      ativos.push({
        propertyName: 'from',
        label: 'from',
        type: 'date',
        value: f.from,
        group: 'De',
      });
    }
    if (f.to) {
      ativos.push({ propertyName: 'to', label: 'to', type: 'date', value: f.to, group: 'Até' });
    }

    for (const { chave, grupo } of FILTROS_DE_LISTA) {
      const valores = (f[chave] as string[] | null | undefined) ?? [];
      if (!valores.length) continue;
      ativos.push({
        propertyName: chave,
        label: chave,
        type: 'text',
        value: valores.map(valor => this.rotuloDoValor(chave, valor)).join(', '),
        group: grupo,
      });
    }

    if (f.hasRecording !== null && f.hasRecording !== undefined) {
      ativos.push({
        propertyName: 'hasRecording',
        label: 'hasRecording',
        type: 'text',
        value: f.hasRecording ? 'Sim' : 'Não',
        group: 'Com gravação',
      });
    }
    if (f.hasTranscript !== null && f.hasTranscript !== undefined) {
      ativos.push({
        propertyName: 'hasTranscript',
        label: 'hasTranscript',
        type: 'text',
        value: f.hasTranscript ? 'Sim' : 'Não',
        group: 'Com transcrição',
      });
    }
    if (f.participantAttendedOnly && f.participantEmails?.length) {
      ativos.push({
        propertyName: 'participantAttendedOnly',
        label: 'participantAttendedOnly',
        type: 'text',
        value: 'Sim',
        group: 'Participante entrou na call',
      });
    }
    if (f.onlyUnlinked) {
      ativos.push({
        propertyName: 'onlyUnlinked',
        label: 'onlyUnlinked',
        type: 'text',
        value: 'Sim',
        group: 'Somente sem vínculo com deal',
      });
    }

    this.sendActiveFilters.emit(ativos);
  }

  /** Nome de um valor de filtro para o pill. */
  private rotuloDoValor(chave: keyof MeetingFilterRequest, valor: string): string {
    switch (chave) {
      case 'statuses':
        return MEETING_STATUS_LABELS[valor as MeetingStatus] ?? valor;
      case 'sources':
        return MEETING_SOURCE_LABELS[valor as MeetingSource] ?? valor;
      case 'callTypes':
        return MEETING_CALL_TYPE_LABELS[valor as MeetingCallType] ?? valor;
      case 'clientVisibility':
        return CLIENT_VISIBILITY_FILTER_LABELS[valor as MeetingClientVisibilityFilter] ?? valor;
      case 'companyIds':
        return this.rotulo(this.companyOptions, valor);
      case 'partnerIds':
        return this.rotulo(this.partnerOptions, valor);
      case 'financialInstitutionIds':
        return this.rotulo(this.bankOptions, valor);
      case 'dealIds':
        return this.rotulo(this.dealOptions, valor);
      case 'organizerUserIds':
        return this.rotulo(this.organizerOptions, valor);
      case 'participantEmails':
        return this.rotulo(this.participantOptions, valor, valor);
      default:
        return valor;
    }
  }

  private rotulo(opcoes: Option[], valor: string, padrao = 'Selecionado'): string {
    return opcoes.find(o => o.value === valor)?.label ?? padrao;
  }

  private paraSimNao(valor: boolean | null | undefined): string | null {
    return valor === true ? 'true' : valor === false ? 'false' : null;
  }

  private deSimNao(valor: unknown): boolean | null {
    return valor === 'true' ? true : valor === 'false' ? false : null;
  }

  private criarCheckbox(
    marcado: boolean,
    label = 'Somente sem vínculo com deal',
    name = 'onlyUnlinked'
  ): Checkbox {
    return {
      label,
      name,
      value: 'true',
      isChecked: marcado,
      isDisabled: false,
    };
  }
}
